import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCurrentUser } from "aws-amplify/auth";
import logoFull from "../assets/images/logo_skill-white.25.svg";
import logoSmall from "../assets/images/logo_sb-white.25.svg";
import "./MarketingNavModern.css";

function MarketingNavModern() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activePricingSection, setActivePricingSection] = useState(false);
  const [activeAISection, setActiveAISection] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Scroll detection for shrinking navigation
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer for pricing section
  useEffect(() => {
    const pricingSection = document.getElementById("pricing");
    if (!pricingSection) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setActivePricingSection(entry.isIntersecting);
        });
      },
      { threshold: 0.3, rootMargin: "-100px 0px -100px 0px" },
    );

    observer.observe(pricingSection);
    return () => observer.disconnect();
  }, [location.pathname]);

  // Intersection Observer for AI section
  useEffect(() => {
    const aiSection = document.getElementById("ai-integration");
    if (!aiSection) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setActiveAISection(entry.isIntersecting);
        });
      },
      { threshold: 0.3, rootMargin: "-100px 0px -100px 0px" },
    );

    observer.observe(aiSection);
    return () => observer.disconnect();
  }, [location.pathname]);

  // Close mobile menu on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (mobileMenuOpen && !target.closest(".marketing-nav-modern")) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const checkAuthStatus = async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
    }
  };

  const isActiveNavItem = (path: string) => {
    if (path === "pricing") {
      return location.pathname === "/" && activePricingSection;
    }
    if (path === "ai") {
      return location.pathname === "/" && activeAISection;
    }
    return location.pathname === path;
  };

  const handleScrollTo = (id: string) => {
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: "smooth" });
    }
  };

  // Handle scroll to section on navigation
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const el = document.getElementById(location.state.scrollTo);
        if (el) {
          window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: "smooth" });
        }
      }, 100);
    }
    // legacy support
    if (location.state?.scrollToPricing) {
      setTimeout(() => {
        const el = document.getElementById("pricing");
        if (el) {
          window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: "smooth" });
        }
      }, 100);
    }
  }, [location]);

  return (
    <nav
      className={`marketing-nav-modern ${isScrolled ? "marketing-nav-modern--scrolled" : ""}`}
    >
      <div className="marketing-nav-modern__container">
        {/* Logo */}
        <button
          className="marketing-nav-modern__logo"
          onClick={() => navigate("/")}
          aria-label="Go to SkillBubble homepage"
        >
          <img
            src={isScrolled ? logoSmall : logoFull}
            alt="SkillBubble"
            className="marketing-nav-modern__logo-img"
          />
        </button>

        {/* Desktop Navigation */}
        <div className="marketing-nav-modern__desktop">
          <div className="marketing-nav-modern__links">
            <a
              href="/skill-packages"
              className={`marketing-nav-modern__link ${isActiveNavItem("/skill-packages") ? "marketing-nav-modern__link--active" : ""}`}
              onClick={(e) => { e.preventDefault(); navigate("/skill-packages"); }}
            >
              Skill Packages
            </a>
            <a
              href="/packages"
              className={`marketing-nav-modern__link ${isActiveNavItem("/packages") ? "marketing-nav-modern__link--active" : ""}`}
              onClick={(e) => { e.preventDefault(); navigate("/packages"); }}
            >
              Discover Packages
            </a>
            <a
              href="/frameworks"
              className={`marketing-nav-modern__link ${isActiveNavItem("/frameworks") ? "marketing-nav-modern__link--active" : ""}`}
              onClick={(e) => { e.preventDefault(); navigate("/frameworks"); }}
            >
              Frameworks
            </a>
            <button
              className={`marketing-nav-modern__link ${isActiveNavItem("ai") ? "marketing-nav-modern__link--active" : ""}`}
              onClick={() => handleScrollTo("ai-integration")}
            >
              AI
            </button>
            <button
              className={`marketing-nav-modern__link ${isActiveNavItem("pricing") ? "marketing-nav-modern__link--active" : ""}`}
              onClick={() => handleScrollTo("pricing")}
            >
              Pricing
            </button>
            <a
              href="/contact"
              className={`marketing-nav-modern__link ${isActiveNavItem("/contact") ? "marketing-nav-modern__link--active" : ""}`}
              onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
            >
              Contact
            </a>
          </div>

          <div className="marketing-nav-modern__actions">
            {isAuthenticated ? (
              <button
                className="marketing-nav-modern__button marketing-nav-modern__button--primary"
                onClick={() => navigate("/app")}
              >
                Go to App
              </button>
            ) : (
              <>
                <button
                  className="marketing-nav-modern__button marketing-nav-modern__button--secondary"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </button>
                <button
                  className="marketing-nav-modern__button marketing-nav-modern__button--primary"
                  onClick={() => navigate("/signup")}
                >
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="marketing-nav-modern__mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 12H21M3 6H21M3 18H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="marketing-nav-modern__mobile-menu">
          <a
            href="/skill-packages"
            className="marketing-nav-modern__mobile-link"
            onClick={(e) => { e.preventDefault(); navigate("/skill-packages"); }}
          >
            Skill Packages
          </a>
          <a
            href="/packages"
            className="marketing-nav-modern__mobile-link"
            onClick={(e) => { e.preventDefault(); navigate("/packages"); }}
          >
            Discover Packages
          </a>
          <a
            href="/frameworks"
            className="marketing-nav-modern__mobile-link"
            onClick={(e) => { e.preventDefault(); navigate("/frameworks"); }}
          >
            Frameworks
          </a>
          <button
            className="marketing-nav-modern__mobile-link"
            onClick={() => handleScrollTo("ai-integration")}
          >
            AI
          </button>
          <button
            className="marketing-nav-modern__mobile-link"
            onClick={() => handleScrollTo("pricing")}
          >
            Pricing
          </button>
          <a
            href="/contact"
            className="marketing-nav-modern__mobile-link"
            onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
          >
            Contact
          </a>

          <div className="marketing-nav-modern__mobile-divider" />

          {isAuthenticated ? (
            <button
              className="marketing-nav-modern__mobile-link"
              onClick={() => navigate("/app")}
            >
              Go to App
            </button>
          ) : (
            <>
              <button
                className="marketing-nav-modern__mobile-link"
                onClick={() => navigate("/login")}
              >
                Sign In
              </button>
              <button
                className="marketing-nav-modern__mobile-link marketing-nav-modern__mobile-link--primary"
                onClick={() => navigate("/signup")}
              >
                Get Started
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

export default MarketingNavModern;
